
import React from 'react';
import { Type, Maximize, MousePointer2, X, Move, SlidersHorizontal, Trash2 } from 'lucide-react';
import { DynamicStyle } from '../types';

interface DesignInspectorProps {
  elementId: string;
  style: DynamicStyle;
  onUpdateStyle: (id: string, style: DynamicStyle) => void;
  onClose: () => void;
  onReset: (id: string) => void;
}

const FONT_FAMILIES = [
  { name: 'Luxury Serif', value: 'var(--font-heading)' },
  { name: 'Modern Sans', value: 'var(--font-main)' },
  { name: 'Elegant Lora', value: "'Lora', serif" },
  { name: 'Minimal Inter', value: "'Inter', sans-serif" },
  { name: 'Playfair Display', value: "'Playfair Display', serif" },
];

const DesignInspector: React.FC<DesignInspectorProps> = ({ elementId, style, onUpdateStyle, onClose, onReset }) => {
  const handleChange = (key: keyof DynamicStyle, value: any) => {
    onUpdateStyle(elementId, { ...style, [key]: value });
  };

  return (
    <div className="fixed top-24 right-8 z-[100] w-72 bg-white/95 backdrop-blur-2xl rounded-[2rem] shadow-[0_32px_64px_-16px_rgba(0,0,0,0.1)] border border-slate-200 overflow-hidden animate-in slide-in-from-right-12 fade-in duration-500">
      <div className="p-5 border-b border-slate-100 flex items-center justify-between bg-slate-50/50">
        <div className="flex items-center gap-2">
          <SlidersHorizontal className="w-4 h-4 text-sky-500" />
          <span className="text-[10px] font-bold uppercase tracking-luxury text-slate-800">Inspector</span>
        </div>
        <div className="flex items-center gap-1">
          <button onClick={() => onReset(elementId)} className="p-2 hover:bg-red-50 text-slate-400 hover:text-red-500 rounded-lg transition-all" title="Reset styles">
            <Trash2 className="w-4 h-4" />
          </button>
          <button onClick={onClose} className="p-2 hover:bg-slate-200 text-slate-400 rounded-lg transition-all">
            <X className="w-4 h-4" />
          </button>
        </div>
      </div>

      <div className="p-6 space-y-8 overflow-y-auto max-h-[60vh] no-scrollbar">
        <div className="space-y-1">
          <span className="text-[9px] font-bold uppercase text-slate-400 tracking-widest">Target Element</span>
          <p className="text-[11px] font-mono text-sky-600 truncate bg-sky-50 px-2 py-1 rounded">#{elementId}</p>
        </div>

        {/* Typography Section */}
        <div className="space-y-5">
          <div className="flex items-center gap-2 mb-2">
            <Type className="w-3.5 h-3.5 text-slate-400" />
            <span className="text-[10px] font-bold uppercase tracking-widest text-slate-800">Typography</span>
          </div>
          
          <div className="space-y-2">
            <label className="text-[9px] font-bold text-slate-500 uppercase">Font Family</label>
            <select 
              value={style.fontFamily || ''} 
              onChange={(e) => handleChange('fontFamily', e.target.value)}
              className="w-full bg-slate-50 border border-slate-200 rounded-xl px-3 py-2 text-xs focus:outline-none focus:border-sky-500 transition-all appearance-none cursor-pointer"
            >
              <option value="">Default</option>
              {FONT_FAMILIES.map(font => (
                <option key={font.value} value={font.value}>{font.name}</option>
              ))}
            </select>
          </div>

          <div className="space-y-2">
            <div className="flex justify-between items-center">
              <label className="text-[9px] font-bold text-slate-500 uppercase">Font Size</label>
              <span className="text-[10px] font-mono text-sky-500">{style.fontSize || '1em'}</span>
            </div>
            <input 
              type="range" min="12" max="160" step="1"
              value={parseInt(style.fontSize || '16')} 
              onChange={(e) => handleChange('fontSize', `${e.target.value}px`)}
              className="w-full accent-sky-500 h-1 bg-slate-100 rounded-lg appearance-none cursor-pointer"
            />
          </div>
        </div>

        {/* Layout Section */}
        <div className="space-y-5">
          <div className="flex items-center gap-2 mb-2">
            <Maximize className="w-3.5 h-3.5 text-slate-400" />
            <span className="text-[10px] font-bold uppercase tracking-widest text-slate-800">Dimensions</span>
          </div>

          <div className="space-y-2">
            <div className="flex justify-between items-center">
              <label className="text-[9px] font-bold text-slate-500 uppercase">Scale</label>
              <span className="text-[10px] font-mono text-sky-500">{(style.scale || 1).toFixed(2)}x</span>
            </div>
            <input 
              type="range" min="0.5" max="2" step="0.05"
              value={style.scale || 1} 
              onChange={(e) => handleChange('scale', parseFloat(e.target.value))}
              className="w-full accent-sky-500 h-1 bg-slate-100 rounded-lg appearance-none cursor-pointer"
            />
          </div>

          <div className="space-y-2">
            <div className="flex justify-between items-center">
              <label className="text-[9px] font-bold text-slate-500 uppercase">Container Width</label>
              <span className="text-[10px] font-mono text-sky-500">{style.width || '100%'}</span>
            </div>
            <input 
              type="range" min="20" max="100" step="1"
              value={parseInt(style.width || '100')} 
              onChange={(e) => handleChange('width', `${e.target.value}%`)}
              className="w-full accent-sky-500 h-1 bg-slate-100 rounded-lg appearance-none cursor-pointer"
            />
          </div>
        </div>
      </div>

      <div className="p-5 bg-slate-50 border-t border-slate-100">
        <p className="text-[9px] text-slate-400 italic leading-relaxed text-center">
          "Design is not just what it looks like and feels like. Design is how it works."
        </p>
      </div>
    </div>
  );
};

export default DesignInspector;
